import { api } from 'controllers/network/apiClient';
import {
  NotificationSubscription,
  RemoveNotificationSubscriptionPayload,
  ShopRole,
  User,
  UserNotification,
  UserNotificationsPreference,
  UserProfile,
} from './types';

const usersUrl = '/v2/users/';
const userProfileUrl = '/v2/users/profile';
const notificationsUrl = '/v2/notifications';

export async function getUsers(): Promise<User[]> {
  const { users } = await api.get(usersUrl);
  return users;
}

export interface AddShopUserPayload {
  username: string;
  roles: number[];
  // Optional so the legacy UserFormModal can send both fields during the
  // RBAC-014 migration. Once RBAC-014 soaks in prod the numeric `roles`
  // field can be dropped entirely.
  roleNames?: string[];
}

export interface AddShopUserByNamePayload {
  username: string;
  roleNames: string[];
}

export async function addUser({
  username,
  roles,
  roleNames,
}: AddShopUserPayload): Promise<void> {
  await api.post(usersUrl, {
    username,
    roles,
    ...(roleNames !== undefined && { role_names: roleNames }),
  });
}

export async function addUserByRoleNames({
  username,
  roleNames,
}: AddShopUserByNamePayload) {
  await api.post(usersUrl, { username, role_names: roleNames });
}

export async function updateUser(user: User): Promise<User> {
  await api.put(usersUrl, user);
  return user;
}

export async function deleteUser(guid: string): Promise<void> {
  await api.delete(`${usersUrl}${guid}`);
}

export async function getUserProfile(): Promise<UserProfile> {
  const profile = await api.get(userProfileUrl);
  return profile;
}

export async function updateUserProfile(user: User): Promise<User> {
  await api.put(userProfileUrl, {
    ...user.profile,
    // We cannot send an empty string as the value of avatarUrl
    avatarUrl: user.profile?.avatarUrl || undefined,
  });
  return user;
}

export async function getShopRoles(
  useAuthService: boolean,
): Promise<ShopRole[]> {
  // When the RBAC_ROLES labs flag is on, call the auth service directly via
  // Traefik's /auth path-prefix rule (same origin as internal-api). Otherwise
  // fall back to the monolith proxy, which returns numeric DB IDs required by
  // the legacy UserFormModal. Remove this split when RBAC-014 deletes the
  // legacy modal.
  const url = useAuthService ? '/auth/roles/shop' : '/v2/users/shop_roles';
  const { roles } = await api.get(url);
  return roles;
}

export interface UpdateUserShopRolesPayload {
  userId: string;
  shopId: number;
  roles: number[];
  // See AddShopUserPayload.roleNames — dual-field submission so the legacy
  // UserFormModal works both before and after RBAC-014 collapses the backend
  // flag.
  roleNames?: string[];
}

export interface UpdateUserShopRolesByNamePayload {
  userId: string;
  shopId: number;
  roleNames: string[];
}

export async function updateUserShopRoles({
  userId,
  shopId,
  roles,
  roleNames,
}: UpdateUserShopRolesPayload): Promise<void> {
  await api.put(`/v2/users/${userId}/shop/${shopId}/roles`, {
    roles,
    ...(roleNames !== undefined && { role_names: roleNames }),
  });
}

export async function updateUserShopRolesByName({
  userId,
  shopId,
  roleNames,
}: UpdateUserShopRolesByNamePayload): Promise<void> {
  await api.put(`/v2/users/${userId}/shop/${shopId}/roles`, {
    role_names: roleNames,
  });
}

export async function getUserPhoneNumber(
  userUuid: string,
  shopId: number,
): Promise<string> {
  const { phones } = await api.get(`${usersUrl}${userUuid}/shop/${shopId}`);
  const { number } = phones[0] ?? '';
  return number;
}

export interface UpdateUserPhoneNumberPayload {
  userUuid: string;
  phoneNumber: string;
}

export async function updateUserPhoneNumber({
  userUuid,
  phoneNumber,
}: UpdateUserPhoneNumberPayload): Promise<void> {
  await api.put(`${usersUrl}${userUuid}/phone`, { number: phoneNumber });
}

export async function getUserNotificationPreferences(): Promise<UserNotificationsPreference> {
  return api.get(`${notificationsUrl}/preferences`);
}

export async function addNotificationSubscription({
  userUuid,
  shopId,
  notificationName,
  notificationChannelType,
}: NotificationSubscription): Promise<UserNotification> {
  return api.put(
    `${notificationsUrl}/${userUuid}/${shopId}/${notificationName}/${notificationChannelType}`,
  );
}

export async function removeNotificationSubscription({
  userUuid,
  shopId,
  notificationName,
  notificationChannelType,
}: NotificationSubscription): Promise<RemoveNotificationSubscriptionPayload> {
  return api.delete(
    `${notificationsUrl}/${userUuid}/${shopId}/${notificationName}/${notificationChannelType}`,
  );
}
